import React, { useState, useEffect } from 'react'
import api from '../../utils/apiClient'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/16/solid'
import { CheckIcon } from '@heroicons/react/20/solid'
import SidePanel from '../SidePanel/SidePanel'
import AuthNavBar from '../Dashboard/AuthNavBar'
import KPICards from './KPICards/KPICards'
import Charts from './Charts/Charts'
import TopBrokerages from './TopBrokerages/TopBrokerages'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const timePeriodOptions = [
	{ id: '24h', name: 'Last 24 Hours', shortName: '24 Hours' },
	{ id: '7d', name: 'Last 7 Days', shortName: '7 Days' },
]

function EmailAnalytics() {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [isPanelOpen, setIsPanelOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [stats, setStats] = useState(null)
	const [chartData, setChartData] = useState(null)
	const [brokerages, setBrokerages] = useState(null)
	const [error, setError] = useState(null)
	const [timePeriod, setTimePeriod] = useState('7d')
	const [selectedAccount, setSelectedAccount] = useState('all')
	const [emailAccounts, setEmailAccounts] = useState([])

	useEffect(() => {
		fetchEmailAccounts()
	}, [])

	useEffect(() => {
		fetchAnalyticsData()
	}, [timePeriod, selectedAccount])

	const fetchEmailAccounts = async () => {
		try {
			const response = await api.get('/api/email/accounts')

			setEmailAccounts(response.data.accounts || [])
		} catch (err) {
			console.error('Failed to fetch email accounts:', err)
		}
	}

	const fetchAnalyticsData = async () => {
		try {
			setIsLoading(true)
			setError(null)

			const params = new URLSearchParams({
				period: timePeriod,
				...(selectedAccount !== 'all' && { account: selectedAccount }),
			})

			const response = await api.get(`/api/emails/analytics?${params}`)

			setStats(response.data.stats)
			setChartData(response.data.chartData)
			setBrokerages(response.data.topBrokerages)
		} catch (err) {
			setError('Failed to load analytics data')
			console.error('Analytics error:', err)
		} finally {
			setIsLoading(false)
		}
	}

	const selectedTimePeriodOption = timePeriodOptions.find(option => option.id === timePeriod) || timePeriodOptions[1]
	const accountOptions = [{ _id: 'all', email: 'All Accounts' }, ...emailAccounts]
	const selectedAccountOption = accountOptions.find(account => (account._id === 'all' ? 'all' : account.email) === selectedAccount) || accountOptions[0]

	const listboxBtnCls = isLight
		? 'grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1 pr-2 pl-2.5 text-left text-sm leading-5 text-gray-900 border border-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-500'
		: 'grid w-full cursor-default grid-cols-1 rounded-md bg-white/5 py-1 pr-2 pl-2.5 text-left text-sm leading-5 text-white outline-1 -outline-offset-1 outline-white/10 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-500'
	const listboxOptionsCls = isLight
		? 'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base border border-gray-200 shadow-lg data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm'
		: 'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-800 py-1 text-base border border-white/20 shadow-lg shadow-indigo-500/20 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm'
	const listboxOptionCls = isLight
		? 'group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-indigo-500 data-focus:text-white data-focus:outline-hidden'
		: 'group relative cursor-default py-2 pr-9 pl-3 text-white select-none data-focus:bg-indigo-500 data-focus:outline-hidden'
	const chevronCls = isLight
		? 'col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4'
		: 'col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-400 sm:size-4'

	return (
		<div className={`min-h-screen ${isLight ? 'bg-white' : 'bg-[#15202b]'}`}>
			<AuthNavBar />
			<SidePanel
				isOpen={isPanelOpen}
				onOpen={() => setIsPanelOpen(true)}
				onClose={() => setIsPanelOpen(false)}
			/>

			<main className="max-w-7xl mx-auto px-4 pt-[5vh] pb-8 sm:px-6 lg:px-8">
				<div className="mb-4 flex items-center justify-between">
					<h1 className={`hidden sm:block text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-white'}`}>Email Analytics</h1>

					<div className="flex gap-2 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end">
						{/* Time Period Filter */}
						<Listbox value={selectedTimePeriodOption} onChange={option => setTimePeriod(option.id)}>
							<div className="relative w-28 sm:w-40">
								<ListboxButton className={listboxBtnCls}>
									<span className="col-start-1 row-start-1 truncate pr-6">
										<span className="sm:hidden">{selectedTimePeriodOption.shortName}</span>
										<span className="hidden sm:inline">{selectedTimePeriodOption.name}</span>
									</span>
									<ChevronUpDownIcon
										aria-hidden="true"
										className={chevronCls}
									/>
								</ListboxButton>

								<ListboxOptions
									transition
									className={listboxOptionsCls}
								>
									{timePeriodOptions.map(option => (
										<ListboxOption
											key={option.id}
											value={option}
											className={listboxOptionCls}
										>
											{({ selected }) => (
												<>
													<span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
														{option.name}
													</span>

													{selected && (
														<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-400 group-data-focus:text-white">
															<CheckIcon aria-hidden="true" className="size-5" />
														</span>
													)}
												</>
											)}
										</ListboxOption>
									))}
								</ListboxOptions>
							</div>
						</Listbox>

						{/* Account Filter */}
						<Listbox
							value={selectedAccountOption}
							onChange={option => setSelectedAccount(option._id === 'all' ? 'all' : option.email)}
						>
							<div className="relative w-36 sm:w-52">
								<ListboxButton className={listboxBtnCls}>
									<span className="col-start-1 row-start-1 truncate pr-6">{selectedAccountOption.email}</span>
									<ChevronUpDownIcon
										aria-hidden="true"
										className={chevronCls}
									/>
								</ListboxButton>

								<ListboxOptions
									transition
									className={listboxOptionsCls}
								>
									{accountOptions.map(account => (
										<ListboxOption
											key={account._id}
											value={account}
											className={listboxOptionCls}
										>
											{({ selected }) => (
												<>
													<span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
														{account.email}
													</span>

													{selected && (
														<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-400 group-data-focus:text-white">
															<CheckIcon aria-hidden="true" className="size-5" />
														</span>
													)}
												</>
											)}
										</ListboxOption>
									))}
								</ListboxOptions>
							</div>
						</Listbox>
					</div>
				</div>

				{isLoading ? (
					<div className="flex items-center justify-center h-64">
						<div className={`animate-spin rounded-full h-12 w-12 border-2 border-t-[#1d9bf0] ${isLight ? 'border-gray-200' : 'border-gray-600'}`}></div>
					</div>
				) : error ? (
					<div className={`text-center p-8 rounded-2xl ${isLight ? 'text-red-600 bg-white border border-gray-200' : 'text-red-400 bg-gray-800 border border-gray-700'}`}>
						{error}
					</div>
				) : (
					<>
						<KPICards stats={stats} />

						<Charts chartData={chartData} timePeriod={timePeriod} />

						<TopBrokerages brokerages={brokerages} timePeriod={timePeriod} selectedAccount={selectedAccount} />
					</>
				)}
			</main>
		</div>
	)
}

export default EmailAnalytics
